import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import {
  StyleSheetManager,
  ThemeProvider,
  createGlobalStyle,
} from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import { ANALYTICS_PROPERTY, AVAILABLE_BRAND_FEATURES } from '@kouto/types';
import { fetchBrandIntegrations, fetchBrandSettings } from 'actions/brand';
import {
  loadPersistedCartItems,
  noNeedToLoadPersistedCartItems,
  resetCart,
} from 'actions/cart';
import { RESTORE_PERSISTED_DISCOUNT_CODE } from 'types/discount-code';
import { RESTORE_PERSISTED_CREDIT_CODES } from 'types/credit-codes';
import {
  CHECKOUT_PRICING_LS_KEY,
  getPersistedCheckoutPricing,
  setActiveBrandId,
} from 'utils/checkoutPricingStorage';
import { setThemeColor, setThemeFont, setThemeMode } from 'actions/theme';
import { useAppState, useBrandId, useDispatch } from 'AppProvider';
import { BookingConfirmation } from 'components/Booking/BookingConfirmation';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import Experiences from 'components/Experiences';
import ExperienceView from 'components/ExperienceView';
import ExperienceViewWrapper from 'components/ExperienceView/ExperienceViewWrapper';
import DraftPreviewBanner from 'components/DraftPreviewBanner';
import { FeatureFlagWrapper } from 'components/FeatureFlagWrapper';
import PreviewBanner from 'components/PreviewBanner';
import Toast from 'components/Toast/Toast';
import ToggleFeature from 'components/ToggleFeature';
import Version from 'components/Version';
import { analyticsManager, checkoutDiagnostics } from 'features/analytics';
import { useEmbedConfig } from 'features/EmbedConfig';
import MainLandingPage from 'features/LandingPage/MainLandingPage';
import {
  Checkout,
  JoinWaitlist,
  ParticipantsForm,
  WaitlistForm,
} from 'features/ShoppingCart/pages';
import useSearchQueryParams from 'hooks/use-search-params';
import useHyattAuthenticateUser from 'hooks/useHyattAuthenticateUser';
import { useIsBrandV2 } from 'hooks/useIsV2Brand';
import { useIsBrandV3 } from 'hooks/useIsV3Brand';
import { generateShades } from 'theme/themePalette';
import { generateVariableCss } from 'theme/themeUtils';
import { STAGE_LIVE } from 'types/app';
import { FETCH_SETTINGS_FAILURE, THEMES } from 'types/brand';
import { CART_LS_KEY } from 'types/cart';
import { getContrastShades, serializeParams } from 'utils';
import { getFontFamily } from 'utils/fonts';
import { getAccessibleFocusColor } from 'utils/colors';
import { getHomeComponent } from 'utils/getHomeComponent';
import { getSearchComponent } from 'utils/getSearchComponent';
import useStorefrontHome from 'hooks/useStorefrontHome';
import { getLSItem } from 'utils/localStorage';
import { useHandleSocialUrl } from 'utils/useHandleSocialUrl';
import GuestReschedulePage from 'features/GuestReschedule/GuestReschedulePage';
import GuestRescheduleConfirmationPage from 'features/GuestReschedule/GuestRescheduleConfirmationPage';
import GuestRescheduleDatePage from 'features/GuestReschedule/GuestRescheduleDatePage';
import GuestRescheduleSuccessPage from 'features/GuestReschedule/GuestRescheduleSuccessPage';
import { useMemberAnalyticsTracker } from 'hooks/useMemberAnalyticsTracker';
import useFetchMemberInfo from 'hooks/useMemberInfo';
import checkForSupportedLanguageCode from 'utils/data/language/checkForSupportedLanguageCode';
import { ScheduleViewPage } from 'features/ScheduleView';
import SelectAddOnsPage from './features/AddOns/SelectAddOnsPage';
import EventPage from './features/Events/EventPage';
import EventClaimTickets from './features/Events/pages/EventClaimTickets';
import EventClaimTicketsError from './features/Events/pages/EventClaimTicketsError';
import GuestCancellationConfirmationPage from './features/GuestCancellation/GuestCancellationConfirmationPage';
import GuestCancellationPage from './features/GuestCancellation/GuestCancellationPage';
import { ReserveCollection } from './features/Reserve/ReserveCollection';
import { useGeneralAnalyticsTracker } from './hooks/useGeneralAnalyticsTracker';
import { RequestToBookConfirmation } from './components/pages/RequestToBookConfirmation';
import { Recommendations } from './components/pages/Recommendations';

// Lazy: nothing in production links to /catalogs yet, so existing pages should not carry its bundle.
const CatalogPage = lazy(() =>
  import('features/Catalogs').then((m) => ({ default: m.CatalogPage })),
);
// The storefront switch (useStorefrontHome): the root catalog on the homepage's own routes.
const StorefrontHomePage = lazy(() =>
  import('features/Catalogs').then((m) => ({
    default: m.StorefrontHomePage,
  })),
);

const GlobalStyle = createGlobalStyle`
  #kouto-embed-root {
    background-color: ${({ darkMode }) =>
      darkMode ? 'var(--way-palette-black-100)' : 'inherit'};
    direction: ${({ isRtl }) => (isRtl ? 'rtl' : 'ltr')};
    text-align: ${({ isRtl }) => (isRtl ? 'right' : 'left')};

    h1,h2,h3,h4,h5,h6{
      text-align: ${({ isRtl }) => (isRtl ? 'right' : 'left')};
    }

    :focus {
      outline: 2px solid var(--way-colors-focusBorderColor);
    }

    /* Remove focus outline for non-visible focus states */
    /* Exclude elements with tabindex="-1" */
    :focus:not(:focus-visible),
      [tabindex="-1"]:focus {
        outline: none;
    }

    ${({ themeVariables }) => themeVariables}
  }

  /* Carry the client background (inherited at #kouto-embed-root) down as a concrete
     value so descendants that opt into background: inherit — e.g. sticky bars — resolve
     to the client color instead of transparent. Visually neutral for these pass-through
     containers. */
  #kouto-app-root {
    background-color: inherit;
  }

  body {
    margin: 0;
    padding: 0;
  }

  *:before,*:after {
    box-sizing: border-box;
  }

  .site-wrapper{
    overflow: visible !important;
  }
  /* Specific fixes for Tourist Site */
  #s_page #kouto-embed-root ul{
    margin-left: 0;
  }
  #s_page #kouto-embed-root li{
    padding-top:0;
    text-align:center;
  }

  body.way-modal-open {
    height: 100%;
    overflow: hidden;
  }

  svg * {
    transition: fill 0.2s, stroke 0.2s;
  }
`;

const CustomScriptStyle = createGlobalStyle`
  ${({ customStyle }) => customStyle};
`;

// Global styles must not get the #kouto-embed-root namespace set in index.js
// (rules like `body {}` and brand custom CSS would stop matching). A nested
// StyleSheetManager reuses the parent's stylis instance unless one of its own
// stylis props is set, so the explicit empty plugin list forces a fresh one
// without the namespace
const NO_STYLIS_PLUGINS = [];

const RTL_LANGUAGES = ['ar'];

const I18N_TO_BCP47_LANG = {
  cz: 'cs',
  jp: 'ja',
};

const toBcp47Lang = (lang) => I18N_TO_BCP47_LANG[lang.toLowerCase()] ?? lang;

const restoreCheckoutPricing = (dispatch, checkoutPricing) => {
  dispatch({
    type: RESTORE_PERSISTED_DISCOUNT_CODE,
    payload: { discountCode: checkoutPricing?.discountCode ?? null },
  });
  dispatch({
    type: RESTORE_PERSISTED_CREDIT_CODES,
    payload: { creditCodes: checkoutPricing?.creditCodes ?? [] },
  });
};

const parseCheckoutPricingStorageValue = (value, brandId) => {
  if (!value) return null;

  const checkoutPricing = JSON.parse(value);
  return checkoutPricing?.[brandId] ?? null;
};

const hasCheckoutPricingCodes = (checkoutPricing) =>
  !!checkoutPricing?.discountCode || !!checkoutPricing?.creditCodes?.length;

export default function App() {
  const { theme, brand } = useAppState();
  const dispatch = useDispatch();
  const brandId = useBrandId();
  const { i18n: currentI18n } = useTranslation();
  const isRtl = RTL_LANGUAGES.some((lang) => {
    const language = currentI18n.language?.toLowerCase();
    return language === lang || language?.startsWith(`${lang}-`);
  });
  const [brandError, setBrandError] = useState(null);
  const [themeVariablesCss, setThemeVariablesCss] = useState('');

  useGeneralAnalyticsTracker();
  useMemberAnalyticsTracker();

  useHandleSocialUrl();

  useFetchMemberInfo({});

  const embedConfig = useEmbedConfig();
  const history = useHistory();

  useEffect(() => {
    if (!embedConfig.experienceId) {
      return;
    }

    if (embedConfig.routerType === 'hash') {
      const currentHash = window.location.hash;
      const [, existingQuery = ''] = currentHash.split('?');
      const querySuffix = existingQuery ? `?${existingQuery}` : '';
      const desiredHash = `#!/e/${embedConfig.experienceId}${querySuffix}`;
      if (currentHash !== desiredHash) {
        window.location.hash = desiredHash;
      }
      return;
    }

    if (embedConfig.routerType === 'query') {
      // The query history preserves the client's existing params on replace.
      history.replace(`/e/${embedConfig.experienceId}`);
    }
  }, [embedConfig.experienceId, embedConfig.routerType, history]);

  const { searchParams } = useSearchQueryParams();

  const isV2Brand = useIsBrandV2();
  const isV3Brand = useIsBrandV3();
  const isV2OrV3Brand = isV2Brand || isV3Brand;

  const newLandingPageFeatureFlag = useBrandToggleFeature(
    AVAILABLE_BRAND_FEATURES.ENABLE_NEW_LANDING_PAGE,
  );

  const newLandingPageEnabled =
    searchParams.v2 === 'true' || newLandingPageFeatureFlag;

  useEffect(() => {
    if (brand?.fetchSettingsStatus !== 'success') return;

    const root = document.getElementById('kouto-embed-root');

    const { classList } = root || {};

    if (classList?.contains('kouto-v1') || classList?.contains('kouto-v2')) {
      return;
    }

    root?.classList.add(newLandingPageEnabled ? 'kouto-v2' : 'kouto-v1');
  }, [brand?.fetchSettingsStatus, newLandingPageEnabled, searchParams?.v2]);

  useEffect(() => {
    const lang = currentI18n.resolvedLanguage;
    if (!lang) return;
    const root = document.getElementById('kouto-embed-root');
    root?.setAttribute('lang', toBcp47Lang(lang));
  }, [currentI18n.resolvedLanguage]);

  const HomeComponent = useMemo(
    () =>
      getHomeComponent({
        homeComponent: embedConfig.home,
        newLandingPageEnabled,
        isV2OrV3Brand,
      }),
    [embedConfig.home, newLandingPageEnabled, isV2OrV3Brand],
  );
  // `/` and `/search` draw the root catalog when the storefront switch is on; the product
  // landing pages have no catalog counterpart and keep the homepage component.
  const storefrontHome = useStorefrontHome();
  const SearchComponent = useMemo(
    () =>
      getSearchComponent({
        homeComponent: embedConfig.home,
        newLandingPageEnabled,
        isV2OrV3Brand,
      }),
    [embedConfig.home, newLandingPageEnabled, isV2OrV3Brand],
  );

  const withStorefrontHome = useCallback(
    (Component) => (props) =>
      storefrontHome.isEnabled ? (
        <Suspense fallback={null}>
          <StorefrontHomePage />
        </Suspense>
      ) : (
        Component && <Component {...props} />
      ),
    [storefrontHome.isEnabled],
  );
  const renderHome = useMemo(
    () => withStorefrontHome(HomeComponent),
    [withStorefrontHome, HomeComponent],
  );
  const renderSearch = useMemo(
    () => withStorefrontHome(SearchComponent),
    [withStorefrontHome, SearchComponent],
  );

  const isAppLive = process.env.KOUTO_STAGE === STAGE_LIVE;

  const firstFetch = async () => {
    setActiveBrandId(brandId);
    const persistedCartState = getLSItem(CART_LS_KEY);

    const response = await dispatch(fetchBrandSettings(brandId));
    if (response.type === FETCH_SETTINGS_FAILURE) {
      setBrandError('Error Fetching Brand');
      return;
    }
    const {
      payload: { settings },
    } = response;

    if (persistedCartState[brandId]) {
      // NOTE: old local storage states are (before new stripe element):
      //  {
      //    [brandId]: [array of cartItems]
      //  }
      // New local storage states are:
      //  {
      //    [brandId]: {
      //      cartId: string,
      //      cartItems: [array of cartItems]
      //    }
      //  }
      if (persistedCartState[brandId].cartId) {
        await dispatch(
          loadPersistedCartItems({
            cartId: persistedCartState[brandId].cartId,
            cartIdUpdatedAt: persistedCartState[brandId].cartIdUpdatedAt,
            cartItems: persistedCartState[brandId].cartItems,
            paymentIntentRevision:
              persistedCartState[brandId].paymentIntentRevision ?? 0,
          }),
        );
      } else {
        await dispatch(
          loadPersistedCartItems({
            cartId: uuidv4(),
            cartItems: persistedCartState[brandId],
            paymentIntentRevision: 0,
          }),
        );
      }

      const persistedPricing = getPersistedCheckoutPricing(brandId);
      // A fresh load has no codes, so restoring an empty entry changes nothing
      // and would only bump the payment intent revision.
      if (hasCheckoutPricingCodes(persistedPricing)) {
        restoreCheckoutPricing(dispatch, persistedPricing);
      }
    } else {
      await dispatch(noNeedToLoadPersistedCartItems());
    }
    await dispatch(fetchBrandIntegrations(brandId));

    analyticsManager.setAppData({
      [ANALYTICS_PROPERTY.CurrencyCode]: get(settings, 'currency'),
    });

    initTheme(settings);
  };

  const initTheme = (settings) => {
    const primaryFont = get(settings, 'designElement.primaryFont');
    const secondaryFont = get(settings, 'designElement.secondaryFont');
    const primaryColor =
      get(settings, 'designElement.primaryColor') || theme.colors.primaryColor;
    const secondaryColor =
      get(settings, 'designElement.secondaryColor') ||
      theme.colors.secondaryColor;
    const buttonColor =
      get(settings, 'designElement.buttonColor') || theme.colors.buttonColor;
    const brandThemeMode =
      get(settings, 'designElement.theme') || THEMES.DEFAULT;
    const designElementColors = {
      primaryColorShades: generateShades(primaryColor),
      secondaryColorShades: generateShades(secondaryColor),
      buttonColorShades: generateShades(buttonColor),
    };
    const themeColors = {
      ...(brandThemeMode === THEMES.DARK ? theme.darkColors : theme.colors),
      ...designElementColors,
    };
    delete themeColors.primaryColor;
    delete themeColors.secondaryColor;
    delete themeColors.buttonColor;
    const themeDesign = {
      ...theme.design,
      ...(brandThemeMode === THEMES.DARK ? theme.darkDesign : {}),
    };

    themeColors.primaryColorContrastShades = getContrastShades(primaryColor);
    themeColors.secondaryColorContrastShades =
      getContrastShades(secondaryColor);
    themeColors.buttonColorContrastShades = getContrastShades(buttonColor);

    // Calculate focus color that contrasts well against the brand's button color
    themeColors.focusColor = getAccessibleFocusColor(
      designElementColors.buttonColorShades[100],
    );

    dispatch(setThemeMode(brandThemeMode));
    dispatch(setThemeColor(themeColors));

    setThemeVariablesCss(
      generateVariableCss({
        'way-colors': themeColors,
        'way-palette': theme.palette,
        'way-design': themeDesign,
      }),
    );

    dispatch(
      setThemeFont({
        primaryFont: getFontFamily(primaryFont),
        secondaryFont: getFontFamily(secondaryFont),
      }),
    );
  };

  useEffect(() => {
    firstFetch();
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === CHECKOUT_PRICING_LS_KEY) {
        try {
          const newPricing = parseCheckoutPricingStorageValue(
            event.newValue,
            brandId,
          );
          const oldPricing = parseCheckoutPricingStorageValue(
            event.oldValue,
            brandId,
          );
          // Another brand's codes changed, or this brand's were rewritten as-is
          if (JSON.stringify(newPricing) === JSON.stringify(oldPricing)) return;

          restoreCheckoutPricing(dispatch, newPricing);
          // eslint-disable-next-line no-empty
        } catch {}
        return;
      }

      if (event.key !== CART_LS_KEY) return;
      checkoutDiagnostics.recordStorageCartEvent();

      if (!event.newValue) {
        dispatch(resetCart(brandId));
        return;
      }

      try {
        const newCart = JSON.parse(event.newValue);
        const brandCart = newCart[brandId];
        if (brandCart?.cartId && brandCart?.cartItems) {
          dispatch(
            loadPersistedCartItems({
              cartId: brandCart.cartId,
              cartIdUpdatedAt: brandCart.cartIdUpdatedAt,
              cartItems: brandCart.cartItems,
              fromStorageSync: true,
              paymentIntentRevision: brandCart.paymentIntentRevision ?? 0,
            }),
          );
        }
        // eslint-disable-next-line no-empty
      } catch {}
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [brandId, dispatch]);

  useEffect(() => {
    if (!embedConfig.language) return;

    const detectedLanguage = checkForSupportedLanguageCode(
      currentI18n.language,
    );
    if (!detectedLanguage) {
      i18n.changeLanguage(embedConfig.language);
    }
  }, [embedConfig.language]);

  useHyattAuthenticateUser();

  if (brandError) {
    console.error('Brand Loading Error', brandError);
    return (
      <div>
        <div>
          <h3
            style={{
              color: 'var(--way-palette-charcoal-100)',
            }}
          >
            Error Loading Brand
          </h3>
          <div
            style={{
              padding: '20px',
              backgroundColor: '#eee',
            }}
          >
            <h4>Checklist</h4>
            <ol>
              <li>Whitelist your domain in Way admin dashboard.</li>
              <li>Make sure you use the correct brandId.</li>
              <li>
                If you have CSP policies set in your backend, whitelist Way
                Script connect sources in the policies.
              </li>
              <code>
                <ul>
                  <li>*.kouto.co</li>
                  <li>*.letsway.com</li>
                  <li>*.stripe.com</li>
                  <li>*.imagekit.io</li>
                  <li>*.googleapis.com</li>
                </ul>
              </code>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  /* eslint-disable react/prop-types */
  return (
    <>
      <ThemeProvider theme={theme}>
        <StyleSheetManager stylisPlugins={NO_STYLIS_PLUGINS}>
          <GlobalStyle
            darkMode={
              get(brand, 'settings.designElement.theme') === THEMES.DARK &&
              !isAppLive
            }
            isRtl={isRtl}
            themeVariables={themeVariablesCss}
          />
          {get(brand, 'settings.customStyle') && (
            <CustomScriptStyle
              customStyle={get(brand, 'settings.customStyle')}
            />
          )}
        </StyleSheetManager>
        <Toast />
        <PreviewBanner />
        <DraftPreviewBanner />
        <main style={{ backgroundColor: 'inherit' }}>
          <Switch>
            <Route exact path="/e">
              {typeof newLandingPageEnabled !== 'undefined' &&
                newLandingPageEnabled && <MainLandingPage />}
              {typeof newLandingPageEnabled !== 'undefined' &&
                !newLandingPageEnabled && <Experiences asHomePage />}
            </Route>
            <Route
              exact
              path="/experiences"
              render={() => (
                <Redirect
                  to={{
                    pathname: '/e',
                    search: serializeParams(searchParams),
                  }}
                />
              )}
            />

            <Route
              path="/collection/:collectionId"
              component={ReserveCollection}
            />

            {/* Not `exact`: the filter bar pushes `/catalogs/:catalogId/search` when a chip or a
                date is picked, and the same page renders the filtered result. */}
            <Route
              path="/catalogs/:catalogId"
              render={() => (
                <Suspense fallback={null}>
                  <CatalogPage />
                </Suspense>
              )}
            />

            <Route path="/addons/:cartItemId" component={SelectAddOnsPage} />

            <Route
              path="/carts/:cartId/guest-cancellation"
              component={GuestCancellationPage}
              exact
            />
            <Route
              path="/carts/:cartId/guest-cancellation/booking/:bookingId"
              component={GuestCancellationPage}
              exact
            />
            <Route
              path="/carts/:cartId/guest-cancellation/confirmation"
              component={GuestCancellationConfirmationPage}
              exact
            />

            <Route
              path="/carts/:cartId/guest-reschedule"
              component={GuestReschedulePage}
              exact
            />
            <Route
              path="/carts/:cartId/guest-reschedule/booking/:bookingId"
              component={GuestReschedulePage}
              exact
            />
            <Route
              path="/carts/:cartId/guest-reschedule/booking/:bookingId/date"
              component={GuestRescheduleDatePage}
              exact
            />
            <Route
              path="/carts/:cartId/guest-reschedule/booking/:bookingId/confirmation"
              component={GuestRescheduleConfirmationPage}
              exact
            />
            <Route
              path="/carts/:cartId/guest-reschedule/booking/:bookingId/success"
              component={GuestRescheduleSuccessPage}
              exact
            />

            <Route
              exact
              path="/event/:eventId/claim/:waitlistEntryId"
              component={EventClaimTickets}
            />
            <Route path="/event/:eventId" component={EventPage} />

            <Route
              exact
              path="*/experiences/:experienceId/booking"
              render={(props) => (
                <Redirect
                  to={{
                    pathname: `/e/${props.match.params.experienceId}/booking`,
                    search: serializeParams(searchParams),
                  }}
                />
              )}
            />
            <Route
              exact
              path="/e/booking/confirmation"
              component={BookingConfirmation}
            />
            <Route
              exact
              path="/e/recommendations"
              component={Recommendations}
            />
            <Route
              exact
              path="/request-to-book/confirmation"
              component={RequestToBookConfirmation}
            />
            <Route
              exact
              path="/experiences/:experienceId"
              render={(props) => (
                <Redirect
                  to={{
                    pathname: `/e/${props.match.params.experienceId}`,
                    search: serializeParams(searchParams),
                  }}
                />
              )}
            />
            <Route exact path="/e/:experienceId">
              <ExperienceViewWrapper>
                <ExperienceView />
              </ExperienceViewWrapper>
            </Route>
            <Route exact path="/calendar">
              <ScheduleViewPage />
            </Route>
            <Route
              exact
              path="/e/:experienceId/participants/:cartItemId"
              render={() => (
                <FeatureFlagWrapper featureKey="shoppingCart">
                  <ParticipantsForm />
                </FeatureFlagWrapper>
              )}
            />
            <Route
              exact
              path="/e/:experienceId/waitlist"
              render={() => <WaitlistForm />}
            />
            <Route
              exact
              path="/checkout"
              render={() => (
                <FeatureFlagWrapper featureKey="shoppingCart">
                  <Checkout />
                </FeatureFlagWrapper>
              )}
            />
            <Route
              exact
              path="/join-waitlist"
              render={() => <JoinWaitlist />}
            />
            <Route
              exact
              path="/claim-tickets-error"
              render={() => <EventClaimTicketsError />}
            />
            <Route
              exact
              path="/landing/:product/search"
              component={HomeComponent}
            />
            <Route exact path="/landing/:product" component={HomeComponent} />
            <Route exact path="/search" render={renderSearch} />
            <Route exact render={renderHome} />
          </Switch>
        </main>
        <footer>
          <ToggleFeature featureId="scriptVersion">
            <Version />
          </ToggleFeature>
        </footer>
      </ThemeProvider>
    </>
  );
}
